import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useFetch } from "../lib/useFetch";

interface Window { weekday: number; startTime: string; endTime: string }
interface Worker { id: number; name: string }

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const field = "input mt-0 text-sm";

export default function AvailabilityPanel({ workers }: { workers: Worker[] }) {
  const [workerId, setWorkerId] = useState<number | "">("");
  return (
    <div className="card p-5 space-y-4">
      <div className="flex items-center gap-3">
        <h2 className="text-lg font-semibold text-ink">Weekly availability</h2>
        <select value={workerId} onChange={(e) => setWorkerId(e.target.value ? Number(e.target.value) : "")}
          className={`${field} w-56`}>
          <option value="">Select a worker…</option>
          {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
      </div>
      {workerId !== "" && <Editor key={workerId} workerId={workerId} />}
    </div>
  );
}

function Editor({ workerId }: { workerId: number }) {
  const { data, loading, reload } = useFetch<{ windows: Window[] }>(`/v1/workforce/workers/${workerId}/availability`);
  const [rows, setRows] = useState<Window[]>([]);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (data) setRows(data.windows); }, [data]);

  const update = (i: number, patch: Partial<Window>) =>
    setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows([...rows, { weekday: 0, startTime: "09:00", endTime: "17:00" }]);
  const removeRow = (i: number) => setRows(rows.filter((_, idx) => idx !== i));

  async function save() {
    setError(null);
    if (rows.some((r) => r.startTime >= r.endTime)) { setError("Each window must start before it ends"); return; }
    try {
      await api(`/v1/workforce/workers/${workerId}/availability`, { method: "PUT", body: JSON.stringify({ windows: rows }) });
      setSaved(true); setTimeout(() => setSaved(false), 2000); reload();
    } catch (err) { setError((err as Error).message); }
  }

  if (loading) return <div className="skeleton h-24" />;
  return (
    <div className="space-y-2">
      {rows.length === 0 && <p className="text-sm text-faint">No availability declared — this worker is never flagged unavailable.</p>}
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2">
          <select value={r.weekday} onChange={(e) => update(i, { weekday: Number(e.target.value) })} className={`${field} w-28`}>
            {DAYS.map((d, idx) => <option key={idx} value={idx}>{d}</option>)}
          </select>
          <input type="time" value={r.startTime} onChange={(e) => update(i, { startTime: e.target.value })} className={`${field} w-32`} />
          <span className="text-faint">–</span>
          <input type="time" value={r.endTime} onChange={(e) => update(i, { endTime: e.target.value })} className={`${field} w-32`} />
          <button onClick={() => removeRow(i)} className="text-sm text-red-600 hover:underline">Remove</button>
        </div>
      ))}
      <div className="flex items-center gap-3 pt-1">
        <button onClick={addRow} className="btn-secondary px-3 py-1.5 text-sm">Add window</button>
        <button onClick={save} className="btn-dark text-sm">Save availability</button>
        {saved && <span className="text-sm text-teal-700">Saved</span>}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>
    </div>
  );
}
